import { useQuery } from '@tanstack/react-query';

import type {
  BookingStage,
  DepositCollectionPoint,
  DepositRefundPoint,
  DepositState,
} from './generated/enums';
import { apiFetch } from './client';

export const depositsKey = ['deposit'] as const;

/** Which slice of the register to read. */
export type DepositLedgerView = 'Holding' | 'Owing' | 'All';

/**
 * One booking's deposit.
 *
 * `refundPaid` is summed from the payment ledger and `daysWaiting` derived from the settlement
 * date — neither is stored, for the reason `paidAmount` is not: a customer disputing a refund is
 * disputing a sequence.
 *
 * `refundDue` null and `refundDue` zero mean different things — "not worked out yet" against
 * "nothing owed" — which is the distinction this screen exists to make visible.
 */
export interface DepositLedgerRow {
  bookingId: number;
  bookingNumber: string | null;
  customerId: number;
  customerName: string;
  customerMobile: string;
  stage: BookingStage;
  state: DepositState;
  held: number | null;
  refundDue: number | null;
  refundPaid: number;
  refundOutstanding: number;
  forfeited: number | null;
  collectionPoint: DepositCollectionPoint;
  refundPoint: DepositRefundPoint;
  takenOn: string | null;
  settledOn: string | null;
  daysWaiting: number | null;
}

export interface DepositLedgerTotals {
  /** Money physically in the shop. Deliberately not netted against what has been forfeited. */
  holding: number;
  outstanding: number;
  forfeitedToDate: number;
  bookingsHolding: number;
  bookingsOwing: number;
  oldestWaitingDays: number;
}

export interface DepositLedger {
  totals: DepositLedgerTotals;
  rows: DepositLedgerRow[];
}

export function useDepositLedger(view: DepositLedgerView) {
  return useQuery({
    queryKey: [...depositsKey, { view }],
    queryFn: () => apiFetch<DepositLedger>(`/api/deposit?view=${view}`),
  });
}
